import { useState, useRef, useCallback, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ComplianceAnalysisResult, AnalysisType, SuggestedFix } from '../../shared/types';
import { useAuth } from '../contexts/AuthContext';
import SuggestFixButton from './SuggestFixButton';
import ReportErrorButton from './ReportErrorButton';
import AnalysisFeedback from './AnalysisFeedback';
import VideoComplianceChecklist from './VideoComplianceChecklist';
import { fixMarkdownFormatting, Md, MdBlock } from '../utils/markdown';
import { scoreColor } from '../utils/score';
import { VerdictBadge, RiskBadge } from './ui/badges';
import ScoreRing from './ui/ScoreRing';
import AnalysisIcon from './ui/AnalysisIcons';
import TypologyShape from './ui/TypologyShape';
import BackButton from './ui/BackButton';
import OriginalInputAccordion from './analysis/OriginalInputAccordion';
import SourceItem from './analysis/SourceItem';
import LegalRef from './analysis/LegalRef';
import ActionBar from './analysis/ActionBar';
import SectionBlock from './analysis/SectionBlock';
import MentionsChecklistSection from './analysis/MentionsChecklistSection';

interface Props {
  result: ComplianceAnalysisResult;
  onNewAnalysis: () => void;
  onSuggestionSaved?: (fix: SuggestedFix) => void;
  analysisLabel?: string;
  analysisType?: AnalysisType;
}

/* ── Helpers ─────────────────────────────────────────────── */

/** Collapse list-item lines into continuous prose (summary should never have bullets). */
function cleanSummary(s: string): string {
  return s
    .replace(/\n\s*[-•]\s*/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/* ── Build full analysis text for global sharing ────────── */

function buildFullAnalysisText(result: ComplianceAnalysisResult): string {
  const lines: string[] = [];

  if (result.generalAnswer) {
    lines.push(result.generalAnswer);
  } else {
    lines.push(`Score : ${result.score}/100`);
    lines.push(`Defense : ${result.judgeDefenseScore}/100 | Contradicteur : ${result.judgeContradicteurScore}/100`);
    lines.push('');
  }

  if (result.summary) lines.push(result.summary, '');

  if (result.interdictionsFormelles.length > 0) {
    lines.push('## Interdictions formelles', '');
    result.interdictionsFormelles.forEach(it => { lines.push(`**${it.element}**`, it.explanation, `Ref: ${it.legalBasis}`, ''); });
  }
  if (result.pointsDeVigilance.length > 0) {
    lines.push('## Points de vigilance', '');
    result.pointsDeVigilance.forEach(it => {
      lines.push(`**${it.element}** (${it.riskLevel})`, it.explanation);
      if (it.recommendation) lines.push(`Recommandation : ${it.recommendation}`);
      lines.push(`Ref: ${it.legalBasis}`, '');
    });
  }
  if (result.conformePoints.length > 0) {
    lines.push('## Points conformes', '');
    result.conformePoints.forEach(it => { lines.push(`**${it.element}**`, it.explanation, `Ref: ${it.legalBasis}`, ''); });
  }
  if (result.arbitrageDetails) lines.push('## Arbitrage', '', result.arbitrageDetails);

  return lines.join('\n');
}

/* ── Main component ─────────────────────────────────────── */

export default function ResultsDisplay({ result, onNewAnalysis, onSuggestionSaved, analysisLabel, analysisType }: Props) {
  const { features } = useAuth();
  const isFree = !features.copyText; // Free plan: no copy, no select, no share
  const isGeneralQuestion = !!result.generalAnswer;

  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(result.generalAnswer ?? '');
  const [displayText, setDisplayText] = useState(result.generalAnswer ?? '');
  const [hovered, setHovered] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Sync local text state when the result prop changes — otherwise clicking
  // a different history item keeps the first analysis' answer in the editor
  // (useState's initial value is only applied on mount).
  useEffect(() => {
    setDisplayText(result.generalAnswer ?? '');
    setEditedText(result.generalAnswer ?? '');
    setIsEditing(false);
  }, [result.generalAnswer, result.traceId]);

  // Source scroll/highlight state
  const [highlightedSourceIdx, setHighlightedSourceIdx] = useState<number | null>(null);
  const sourceRefs = useRef<(HTMLLIElement | null)[]>([]);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleRefClick = useCallback((articleRef: string) => {
    // Find the best matching source
    const normalized = articleRef.toUpperCase().replace(/\s+/g, '');
    const idx = result.sources.findIndex(s => {
      const ref = (s.reference + ' ' + s.title).toUpperCase().replace(/\s+/g, '');
      return ref.includes(normalized);
    });

    if (idx === -1) return;

    // Highlight + expand
    setHighlightedSourceIdx(idx);

    // Scroll to source
    setTimeout(() => {
      sourceRefs.current[idx]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);

    // Clear highlight after 3s
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => setHighlightedSourceIdx(null), 3000);
  }, [result.sources]);

  const handleStartEdit = useCallback(() => {
    setEditedText(displayText);
    setIsEditing(true);
    setTimeout(() => textareaRef.current?.focus(), 50);
  }, [displayText]);

  const handleValidate = useCallback(() => {
    setDisplayText(editedText);
    setIsEditing(false);
  }, [editedText]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
  }, []);

  const fullText = buildFullAnalysisText(
    isGeneralQuestion ? { ...result, generalAnswer: displayText } : result,
  );

  const defenseWins = result.judgeDefenseScore >= result.judgeContradicteurScore;

  return (
    <div
      className="animate-fade-in max-w-[760px] mx-auto"
      style={isFree ? { userSelect: 'none', WebkitUserSelect: 'none' } : undefined}
      onCopy={isFree ? (e) => e.preventDefault() : undefined}
    >
      {/* Free tier upgrade banner */}
      {isFree && (
        <div className="mb-6 p-4 rounded-2xl bg-violet-100 border border-violet-200">
          <p className="text-sm text-violet-800 font-medium mb-1">
            Analyse en lecture seule
          </p>
          <p className="text-xs text-violet-600">
            Passez au plan Pro pour copier, partager et conserver vos analyses dans l'historique.
          </p>
        </div>
      )}

      {/* Retour */}
      <BackButton onClick={onNewAnalysis} label="Nouvelle analyse" />

      {/* Bandeau d'en-tête : rappelle la typologie analysée par sa couleur —
          le même aplat que la tuile d'origine et que le formulaire. */}
      {analysisLabel && (
        <div className="hero-tint mb-8 flex items-center gap-4 rounded-3xl px-5 py-5 sm:px-6">
          {analysisType && (
            <span className="hero-pill grid h-12 w-12 shrink-0 place-items-center">
              <TypologyShape type={analysisType} color="currentColor" />
              <AnalysisIcon type={analysisType} className="h-5 w-5" />
            </span>
          )}
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.14em] text-ink/50">R&eacute;sultat d'analyse</p>
            <h2 className="font-display text-[1.5rem] leading-tight text-ink">{analysisLabel}</h2>
          </div>
        </div>
      )}

      {/* ── General Question ──────────────────────────────── */}
      {isGeneralQuestion ? (
        <div className="mb-8">
          {/* Votre question — en haut, comme les autres features */}
          {(result.originalInput || result.originalImages?.length || result.originalLabelUsage || result.originalEvent) && (
            <div className="mb-6">
              <OriginalInputAccordion title={analysisLabel ? `Votre ${analysisLabel.toLowerCase()}` : 'Votre question'} input={result.originalInput} context={result.originalContext} images={result.originalImages} labelUsage={result.originalLabelUsage} event={result.originalEvent} />
            </div>
          )}
          <div className="flex items-center gap-2.5 mb-4">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[var(--tint)] text-ink">
              <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                <path d="M8 9h8" /><path d="M8 13h5" />
              </svg>
            </span>
            <h2 className="font-display text-xl text-slate-900">R&eacute;ponse</h2>
          </div>

          <div
            className="relative group/result"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            <div className={`flex justify-center transition-opacity duration-200 ${hovered && !isEditing ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
              <ActionBar text={displayText} onEdit={handleStartEdit} result={{ ...result, generalAnswer: displayText }} analysisLabel={analysisLabel} analysisType={analysisType} />
            </div>

            <div className="card-tint rounded-3xl px-8 py-8 md:px-10 md:py-10">
              {isEditing ? (
                <div>
                  <div className="glow-input">
                    <textarea
                      ref={textareaRef}
                      value={editedText}
                      onChange={(e) => setEditedText(e.target.value)}
                      className="w-full min-h-[200px] rounded-xl border border-slate-300 bg-cream-app px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-shadow resize-y"
                    />
                  </div>
                  <div className="mt-3 flex gap-2 justify-end">
                    <button onClick={handleCancelEdit} className="btn-soft font-display px-4 py-2 text-sm">Annuler</button>
                    <button onClick={handleValidate} className="btn-ink font-display px-4 py-2 text-sm">Valider</button>
                  </div>
                </div>
              ) : (
                <div className="prose-visaly text-sm text-slate-700 leading-relaxed">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{fixMarkdownFormatting(displayText)}</ReactMarkdown>
                </div>
              )}
            </div>

            <div className={`flex justify-center transition-opacity duration-200 ${hovered && !isEditing ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
              <ActionBar text={displayText} onEdit={handleStartEdit} result={{ ...result, generalAnswer: displayText }} analysisLabel={analysisLabel} analysisType={analysisType} />
            </div>
          </div>

          {result.summary && (
            <div className="mt-6 italic">
              <MdBlock className="text-sm text-slate-500">{cleanSummary(result.summary)}</MdBlock>
            </div>
          )}
        </div>

      /* ── Scored Analysis ────────────────────────────────── */
      ) : (
        <div className="mb-10">
          <div className="text-center">
            <div className="inline-flex flex-col items-center">
              {/* Anneau segmenté — les 4 arcs pastel se remplissent avec le score */}
              <ScoreRing score={result.score} size={200} className="mb-5">
                <span className="font-display text-[3.5rem] leading-none text-slate-900">
                  {result.score}
                </span>
                <span className="mt-1 text-[10px] uppercase tracking-[0.12em] text-slate-400">
                  sur 100
                </span>
              </ScoreRing>
              <VerdictBadge verdict={result.verdict} />
            </div>

            {result.summary && (
              <div className="mt-6 max-w-[600px] mx-auto">
                <MdBlock className="text-sm text-slate-600">{cleanSummary(result.summary)}</MdBlock>
              </div>
            )}

            {/* Pipeline 3 juges — Défense + Contradicteur → Arbitre (verdict final) */}
            <div className="mt-8">
              <div className="flex items-start justify-center gap-2 sm:gap-4 text-sm">
                {/* Juge Défense */}
                <div className="group/def flex flex-col items-center relative max-w-[120px]">
                  <span className={`font-display text-xl text-ink ${defenseWins ? '' : 'opacity-45'}`}>
                    {result.judgeDefenseScore}<span className="text-xs font-normal text-slate-400">/100</span>
                  </span>
                  <span className={`mt-1 cursor-help text-center text-[10px] uppercase tracking-[0.14em] text-slate-500 ${defenseWins ? '' : 'opacity-45'}`}>Juge D&eacute;fense</span>
                  <span className={`mt-1.5 h-[3px] w-9 rounded-full bg-emerald-400 ${defenseWins ? '' : 'opacity-30'}`} />
                  <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-56 px-3 py-2 rounded-xl bg-slate-900 text-white text-[11px] leading-relaxed opacity-0 pointer-events-none group-hover/def:opacity-100 transition-opacity duration-200 z-20">
                    L'avocat de la d&eacute;fense plaide la conformit&eacute;&nbsp;: il cherche tout ce qui est autoris&eacute; par la r&eacute;glementation de la publicit&eacute; des produits de sant&eacute; dans votre contenu.
                  </div>
                </div>

                <span className="text-slate-300 text-base leading-[1.7] select-none">+</span>

                {/* Juge Contradicteur */}
                <div className="group/con flex flex-col items-center relative max-w-[120px]">
                  <span className={`font-display text-xl text-ink ${!defenseWins ? '' : 'opacity-45'}`}>
                    {result.judgeContradicteurScore}<span className="text-xs font-normal text-slate-400">/100</span>
                  </span>
                  <span className={`mt-1 cursor-help text-center text-[10px] uppercase tracking-[0.14em] text-slate-500 ${!defenseWins ? '' : 'opacity-45'}`}>Juge Contradicteur</span>
                  <span className={`mt-1.5 h-[3px] w-9 rounded-full bg-red-400 ${!defenseWins ? '' : 'opacity-30'}`} />
                  <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-56 px-3 py-2 rounded-xl bg-slate-900 text-white text-[11px] leading-relaxed opacity-0 pointer-events-none group-hover/con:opacity-100 transition-opacity duration-200 z-20">
                    Le contradicteur plaide &agrave; charge&nbsp;: il identifie tout ce qui pourrait poser un probl&egrave;me juridique ou &ecirc;tre interpr&eacute;t&eacute; comme non conforme.
                  </div>
                </div>

                <span className="text-slate-300 text-base leading-[1.7] select-none">&rarr;</span>

                {/* Arbitre — verdict final */}
                <div className="group/arb flex flex-col items-center relative max-w-[130px]">
                  <span className={`font-display text-xl ${scoreColor(result.score)}`}>
                    {result.score}<span className="text-xs font-normal text-slate-400">/100</span>
                  </span>
                  <span className="mt-1 inline-flex cursor-help items-center gap-1 text-center text-[10px] font-semibold uppercase tracking-[0.14em] text-ink">
                    Arbitre
                    <svg className="w-3 h-3 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
                  </span>
                  <span className="mt-1.5 h-[3px] w-9 rounded-full bg-ink" />
                  <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-64 px-3 py-2 rounded-xl bg-slate-900 text-white text-[11px] leading-relaxed opacity-0 pointer-events-none group-hover/arb:opacity-100 transition-opacity duration-200 z-20">
                    L'arbitre p&egrave;se les deux plaidoiries et rend le <strong>score final</strong> &mdash; ce n'est pas une moyenne des deux juges. Il &eacute;carte les arguments non &eacute;tay&eacute;s par la loi ou la jurisprudence.
                  </div>
                </div>
              </div>
              <p className="mt-4 text-xs text-slate-400 text-center max-w-[440px] mx-auto">
                Les deux juges plaident, l'arbitre tranche. Le grand score ci-dessus est le verdict de l'arbitre, pas une moyenne.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Original Input ──────────────────────────────────── */}
      {!isGeneralQuestion && (result.originalInput || result.originalImages?.length || result.originalLabelUsage || result.originalEvent) && (
        <div className="mb-8">
          <OriginalInputAccordion title={analysisLabel ? `Votre ${analysisLabel.toLowerCase()}` : 'Votre demande'} input={result.originalInput} context={result.originalContext} images={result.originalImages} labelUsage={result.originalLabelUsage} event={result.originalEvent} />
        </div>
      )}

      {/* Video checklist */}
      {result.socialMeta?.format === 'video' && (
        <div className="mb-8">
          <VideoComplianceChecklist />
        </div>
      )}

      {/* ── Hors périmètre publicité santé (religion / politique / conflit) ──
           Avertissement annexe, n'affecte pas le score. N'apparaît que si
           l'arbitre a détecté une référence explicite. */}
      {!isGeneralQuestion && result.outOfScopeFlags && result.outOfScopeFlags.length > 0 && (
        <div className="mt-8 mb-8 rounded-2xl border border-amber-300 bg-amber-50 px-6 py-5">
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-4 h-4 text-amber-600 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><path d="M12 9v4M12 17h.01" /></svg>
            <h3 className="font-display text-sm text-amber-700 uppercase tracking-wide">Hors périmètre publicité santé</h3>
          </div>
          <p className="text-xs text-slate-500 mb-4">
            Ces points ne relèvent pas de la réglementation de la publicité des produits de santé et <strong>n'affectent pas le score</strong>. Ils signalent un risque juridique distinct (dépôt de marque, image) — à faire valider par un conseil en propriété industrielle.
          </p>
          <ul className="space-y-4">
            {result.outOfScopeFlags.map((flag, i) => (
              <li key={i} className="text-sm">
                <p className="font-semibold text-slate-900 mb-1">
                  {flag.element}
                  <span className="ml-2 align-middle inline-block px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-amber-200 text-ink">{flag.topic}</span>
                </p>
                <p className="text-slate-600 leading-relaxed">{flag.explanation}</p>
                <p className="text-xs text-amber-700 mt-1">Réf.&nbsp;: {flag.legalBasis}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Sections ──────────────────────────────────────── */}
      <div className="space-y-8 stagger-children">
        {/* Interdictions */}
        {!isGeneralQuestion && result.interdictionsFormelles.length > 0 && (
          <SectionBlock
            className="rounded-2xl border border-red-200 bg-red-50/50 px-6 py-6"
            copyText={'## Interdictions formelles\n\n' + result.interdictionsFormelles.map(it => `**${it.element}**\n${it.explanation}\nRef: ${it.legalBasis}`).join('\n\n')}
          >
            <h3 className="font-display text-sm text-red-700 uppercase tracking-wide mb-5">Interdictions formelles</h3>
            <ul className="space-y-6">
              {result.interdictionsFormelles.map((item, i) => (
                <li key={i} className="text-sm leading-relaxed">
                  <p className="font-medium text-slate-900 mb-1"><Md>{item.element}</Md></p>
                  <p className="text-slate-600 leading-relaxed"><Md>{item.explanation}</Md></p>
                  <LegalRef text={item.legalBasis} colorClass="text-red-600" onRefClick={handleRefClick} />
                </li>
              ))}
            </ul>
          </SectionBlock>
        )}

        {/* Points de vigilance */}
        {!isGeneralQuestion && result.pointsDeVigilance.length > 0 && (
          <SectionBlock
            className="rounded-2xl border border-amber-200 bg-amber-50/50 px-6 py-6"
            copyText={'## Points de vigilance\n\n' + result.pointsDeVigilance.map(it => `**${it.element}** (${it.riskLevel})\n${it.explanation}${it.recommendation ? '\nRecommandation : ' + it.recommendation : ''}\nRef: ${it.legalBasis}`).join('\n\n')}
          >
            <h3 className="font-display text-sm text-amber-700 uppercase tracking-wide mb-5">Points de vigilance</h3>
            <ul className="space-y-6">
              {result.pointsDeVigilance.map((item, i) => (
                <li key={i} className="text-sm leading-relaxed">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-slate-900"><Md>{item.element}</Md></span>
                    <RiskBadge level={item.riskLevel} />
                  </div>
                  <p className="text-slate-600 leading-relaxed"><Md>{item.explanation}</Md></p>
                  {item.recommendation && (
                    <div className="mt-2 text-[var(--tint-deep)] text-xs leading-relaxed">
                      <span className="font-semibold">Recommandation :</span>
                      <div className="mt-1"><Md inheritBold>{item.recommendation}</Md></div>
                    </div>
                  )}
                  <LegalRef text={item.legalBasis} colorClass="text-slate-500" onRefClick={handleRefClick} />
                </li>
              ))}
            </ul>
          </SectionBlock>
        )}

        {/* Points conformes */}
        {!isGeneralQuestion && result.conformePoints.length > 0 && (
          <SectionBlock
            className="rounded-2xl border border-emerald-200 bg-emerald-50/50 px-6 py-6"
            copyText={'## Points conformes\n\n' + result.conformePoints.map(it => `**${it.element}**\n${it.explanation}\nRef: ${it.legalBasis}`).join('\n\n')}
          >
            <h3 className="font-display text-sm text-emerald-700 uppercase tracking-wide mb-5">Points conformes</h3>
            <ul className="space-y-6">
              {result.conformePoints.map((item, i) => (
                <li key={i} className="text-sm leading-relaxed">
                  <p className="font-medium text-slate-900 mb-1"><Md>{item.element}</Md></p>
                  <p className="text-slate-600 leading-relaxed"><Md>{item.explanation}</Md></p>
                  <LegalRef text={item.legalBasis} colorClass="text-emerald-600" onRefClick={handleRefClick} />
                </li>
              ))}
            </ul>
          </SectionBlock>
        )}

        {/* Mentions obligatoires — checklist déterministe calculée par règles
             côté serveur (R5122-3). Absente pour les types non concernés. */}
        {!isGeneralQuestion && result.mentionsChecklist && (
          <MentionsChecklistSection checklist={result.mentionsChecklist} />
        )}

        {/* Sources */}
        {result.sources.length > 0 && (
          <SectionBlock
            className="card-tint rounded-2xl px-6 py-6"
            copyText={'## Sources juridiques\n\n' + result.sources.map(s => `[${s.type}] ${s.title} — ${s.reference}`).join('\n')}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-sm uppercase tracking-wide text-[var(--tint-deep)]">Sources juridiques</h3>
              {result.sources.some(s => s.articleContent) && (
                <span className="text-[10px] text-slate-400">Source : DILA / L&eacute;gifrance</span>
              )}
            </div>
            <ul className="space-y-2">
              {result.sources.map((source, i) => (
                <SourceItem
                  key={i}
                  source={source}
                  forceExpanded={highlightedSourceIdx === i}
                  sourceRef={(el: HTMLLIElement | null) => { sourceRefs.current[i] = el; }}
                />
              ))}
            </ul>
          </SectionBlock>
        )}

        {/* Arbitrage Details — rendered as markdown */}
        {!isGeneralQuestion && result.arbitrageDetails && (
          <SectionBlock
            className="card-tint rounded-2xl px-6 py-6"
            copyText={'## Détails de l\'arbitrage\n\n' + result.arbitrageDetails}
          >
            <details>
              <summary className="font-display text-sm cursor-pointer mb-4 text-[var(--tint-deep)]">D&eacute;tails de l'arbitrage</summary>
              <MdBlock className="text-sm text-slate-600">{result.arbitrageDetails}</MdBlock>
            </details>
          </SectionBlock>
        )}
      </div>

      {/* ── Rappel mentions obligatoires pour les analyses d'idées ──
           Nom de produit / claim / concept ne sont pas des supports
           publicitaires : l'absence des mentions obligatoires n'est pas un
           manquement ici, mais le deviendra au moment de la diffusion. */}
      {!isGeneralQuestion &&
        analysisType &&
        (analysisType === 'brand_name' ||
          analysisType === 'slogan' ||
          analysisType === 'concept') && (
          <div className="card-tint mt-6 px-4 py-3 rounded-xl text-xs text-slate-600 leading-relaxed">
            <span className="font-semibold text-[var(--tint-deep)]">Rappel&nbsp;:</span>{' '}
            les mentions obligatoires selon le régime du produit (numéro de visa GP, «&nbsp;Lire attentivement la notice&nbsp;», «&nbsp;Demandez conseil à votre pharmacien&nbsp;»…)
            deviendront obligatoires dès que ce {analysisType === 'brand_name' ? 'nom' : analysisType === 'slogan' ? 'claim' : 'concept'} sera porté sur un support publicitaire (PLV, affiche, campagne, réseaux sociaux). Elles n'ont pas à figurer à ce stade.
          </div>
        )}

      {/* ── Fix auto suggestion (for non-conform results) ──── */}
      {!isGeneralQuestion && analysisType && (
        <SuggestFixButton result={result} analysisType={analysisType} onSaved={onSuggestionSaved} />
      )}

      {/* ── Langfuse feedback (thumbs up/down) ──────────────── */}
      {result.traceId && <AnalysisFeedback traceId={result.traceId} />}

      {/* ── Signalement qualité (canal RGPD art. 22) — uniquement pour les analyses scorées ── */}
      {!isGeneralQuestion && (
        <ReportErrorButton result={result} analysisType={analysisType} />
      )}

      {/* ── Global share bar (hidden on free plan) ─────────── */}
      {!isFree && (
        <div className="mt-8 mb-2 flex justify-center">
          <ActionBar text={fullText} result={isGeneralQuestion ? { ...result, generalAnswer: displayText } : result} analysisLabel={analysisLabel} analysisType={analysisType} />
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-center">
        <button onClick={onNewAnalysis} className="btn-ink font-display px-8 py-3">
          Nouvelle analyse
        </button>
      </div>

      {/* Disclaimer */}
      <div className="mt-6 space-y-2 text-center text-xs text-slate-400">
        <p>
          Cette analyse ne constitue pas un avis juridique. Les textes de loi sont fournis
          {' '}&agrave; titre informatif et ne sont pas opposables &mdash; seuls les PDF sign&eacute;s
          publi&eacute;s au Journal officiel de la R&eacute;publique fran&ccedil;aise font foi.
          Consultez un avocat sp&eacute;cialis&eacute; pour une validation officielle.
        </p>
        <p>
          Donn&eacute;es juridiques : DILA / L&eacute;gifrance &mdash;{' '}
          <a href="https://www.etalab.gouv.fr/wp-content/uploads/2017/04/ETALAB-Licence-Ouverte-v2.0.pdf" target="_blank" rel="noopener noreferrer"
            className="underline hover:text-slate-600">
            Licence Ouverte 2.0
          </a>
        </p>
      </div>
    </div>
  );
}
